import { Component, OnDestroy, OnInit, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  FilterParams,
  ClobSettingsObj
} from '../f1065-k2-data-entry.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f1065-k2-p13s0',
    templateUrl: './f1065-k2-p13s0.component.html',
    styleUrls: ['../f1065-k2.styles.scss', './f1065-k2-p13s0.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065K2P13S0Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  // Configuration - unique per component
  private lineDetailsActionCode = 'w5bepp';
  private linkPart = '_P13_S0';
  sectionHeader = 'Foreign Partner\'s Distributive Share of Deemed Sale Items on Transfer of Partnership Interest:';

  // Data
  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};
  
  colHeaderData: ColumnHeaderData[] = [];
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];
  
  // State
  loadingData = true;
  showLoader = true;
  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isUIErrorSec = '';
  isValidationError = false;
  isInitialized = false;

  // Counters for dropdown loading
  totalDropDownActions = 0;
  loadedActionCounter = 0;

  // Labels
  currentEntityName = '';
  currentPartnerName = '';
  currentCodeGroup = '';
  formSchd = '';

  // Keys
  jcdKey = 0;
  scenario = '';

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };

  private isLoading = false;

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    // Explicit initial call — tryInit() has internal dedup (lastContextKey + isLoading),
    // so redundant calls from BehaviorSubject replay are harmless.
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  private tryInit(): void {
    if (this.isLoading) return;

    const hasGlobalService = this.globalService && this.globalService.globalParams;
    const hasBaseUrl = this.GENERAL_CONFIG?.base_url || (this.baseURLs && this.baseURLs.api);
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;

    if (!hasGlobalService || !hasBaseUrl || !hasFilterParams) {
      return;
    }

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.gtwWorkspace.workspaceFactory?.activeScreen?.link || ''
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    this.init();
  }

  private reloadData(): void {
    this.isLoading = false;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.groupedFormLines = [];
    this.colHeaderData = [];
    this.showErrCol = false;
    this.isValidationError = false;
    this.isUIErrorSec = '';
    
    this.init();
  }

  private init(): void {
    this.isInitialized = true;
    this.scenario = this.filterParams.scenario || '';
    
    this.setCurrentCodeGroup();
    this.loadLockStatus();
  }

  private setCurrentCodeGroup(): void {
    this.currentCodeGroup = `${this.filterParams.p_form}_${this.filterParams.p_schedule}${this.linkPart}`;
    this.formSchd = `${this.filterParams.p_form}_${this.filterParams.p_schedule}`;
  }

  private loadLockStatus(): void {
    this.service.getLockStatus(
      this.baseURLs.api,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (data && data.length > 0 && data[0].LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
        this.loadColumnHeaders();
      },
      error: (err) => {
        console.error('[F1065K2-P13S0] Error loading lock status:', err);
        this.loadColumnHeaders();
      }
    });
  }

  private loadColumnHeaders(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();

    this.service.getColumnHeaders(
      this.baseURLs.api,
      this.filterParams.tax_year || '',
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.colHeaderData = data || [];
        
        if (this.colHeaderData.length === 0) {
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.showLoader = false;
          this.isLoading = false;
          return;
        }
        
        // Process validation info for column headers
        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
        }
        
        this.loadLineDetails();
      },
      error: (err) => {
        console.error('[F1065K2-P13S0] Error loading column headers:', err);
        this.service.showError('Error loading column headers');
        this.showLoader = false;
        this.isLoading = false;
      }
    });
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    const pFilerKey = this.filterParams.p_filer_key;
    if (pFilerKey === null || pFilerKey === undefined || (typeof pFilerKey === 'number' && pFilerKey < 0)) {
      this.service.showError('Please select all the filters');
      this.showLoader = false;
      this.isLoading = false;
      return;
    }

    const isK3 = this.currentCodeGroup.includes('_K3');
    const pFilerPartnerKey = this.filterParams.p_filer_partner_key;
    if (isK3) {
      const partnerKeyInvalid = pFilerPartnerKey === null || pFilerPartnerKey === undefined ||
        (typeof pFilerPartnerKey === 'number' && pFilerPartnerKey < 0) ||
        (typeof pFilerPartnerKey === 'string' && (!pFilerPartnerKey || pFilerPartnerKey.trim() === ''));
      
      if (partnerKeyInvalid) {
        this.service.showError('Please select all the filters');
        this.showLoader = false;
        this.isLoading = false;
        return;
      }
    }

    // Set entity and partner labels for confirmation modal
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (isK3) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }

    this.service.getLineDetails(
      this.baseURLs.api,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      pFilerKey,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);
        
        // Calculate totals on form load (empty for P13)
        for (const line of this.groupedFormLines) {
          this.calcTotalsCol(line.items);
        }

        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.showErrCol = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P13S0] Error loading line details:', err);
        this.service.showError('Error loading line details');
        this.showLoader = false;
        this.isLoading = false;
      }
    });
  }

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (formLines && formLines.length > 0) {
      this.isAdmin = true;
    }

    const grouped: GroupedFormLine[] = [];

    for (const property of formLines) {
      let bucket = grouped.find(b =>
        b.SCHD_KEY === property.SCHD_KEY &&
        b.LINE_KEY === property.LINE_KEY &&
        b.LINE_NO === property.LINE_NO &&
        b.LINE_DESC === property.LINE_DESC &&
        b.LINE_ORDER === property.LINE_ORDER &&
        b.SNO === property.SNO &&
        b.IS_LINE_MULTI === property.IS_LINE_MULTI &&
        b.LINE_SEC === property.LINE_SEC
      );

      if (!bucket) {
        bucket = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI,
          VALIDATION_RULE: property.LINE_VAL,
          LINE_SEC: property.LINE_SEC,
          IS_MANDATORY: 'N',
          ERR_MSG: undefined,
          items: []
        };
        grouped.push(bucket);
      }

      const attribs: FormLineItem = {
        SCHD_KEY: property.SCHD_KEY,
        LINE_KEY: property.LINE_KEY,
        COL_KEY: property.COL_KEY,
        COL_DESC: property.COL_DESC,
        COL_DESC_INFO: property.COL_DESC_INFO,
        COL_ORDER: property.COL_ORDER,
        IS_ENABLED: property.EDITABLE_Y_N,
        SNO: property.SNO,
        ADJ_KEY: property.ADJ_KEY,
        IS_COL_MULTI: property.IS_COL_MULTI,
        UI_COMP: property.UI_COMP,
        LINE_COMP: property.LINE_COMP,
        ACTION_Y_N: property.ACTION_Y_N,
        CELL_VALUE: property.CELL_VALUE,
        IS_MANDATORY: 'N',
        OPERATION_TYPE: undefined,
        IS_ERROR: 'N'
      };

      // Set action data if available
      if (attribs.ACTION_Y_N === 'Y') {
        const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
        if (colObj) {
          attribs.actionData = colObj.actionData;
        }
      }

      // Set validation info on bucket
      this.setValidationInfo(bucket, bucket);

      // Set operation type
      if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
        attribs.OPERATION_TYPE = 'U';
      } else {
        attribs.OPERATION_TYPE = undefined;
      }

      // Set UI component defaults for multi-line empty rows
      if (property.IS_LINE_MULTI === 'Y' && this.isEmptyVal(attribs.CELL_VALUE)) {
        this.setUICompDefaults(attribs);
      }

      // Convert DATE string to Date object
      if (attribs.LINE_COMP === 'DATE' && this.isUICompNotEmpty(attribs)) {
        attribs.CELL_VALUE = parseAsLocalDate(property.CELL_VALUE);
      }

      // Mark non-editable cells as not to be saved
      if (this.isSkipFromSaving(bucket, attribs)) {
        attribs.OPERATION_TYPE = 'N';
      }

      bucket.items.push(attribs);
    }

    return grouped;
  }

  private setValidationInfo(source: any, dest: any): void {
    if (source.VALIDATION_RULE != null) {
      const validationRulesArr = source.VALIDATION_RULE.split(';');
      for (const rule of validationRulesArr) {
        if (rule === 'REQ') {
          dest.IS_MANDATORY = 'Y';
        } else if (rule.startsWith('PREF_REQ')) {
          dest.IS_PREF_MANDATORY = 'Y';
          if (rule.includes(':') && rule.includes('/')) {
            dest.PREF_MAN_COLS = rule.split(':')[1].split('/');
            dest.VALIDATION_MSG = dest.PREF_MAN_COLS[dest.PREF_MAN_COLS.length - 1];
            dest.PREF_MAN_COLS.splice(dest.PREF_MAN_COLS.length - 1, 1);
          }
        } else if (rule.includes('MAX_LEN:')) {
          dest.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  private setUICompDefaults(attrib: FormLineItem): void {
    const comp = attrib.LINE_COMP || attrib.UI_COMP;
    if (comp?.startsWith('NUMERIC')) {
      attrib.CELL_VALUE = null;
    } else if (['TEXT', 'DATE', 'CHK_BX', 'DROP_DOWN'].includes(comp || '')) {
      attrib.CELL_VALUE = '';
    }
  }

  private isEmptyVal(val: any): boolean {
    return val === null || val === '' || val === undefined;
  }

  private isUICompEmpty(attrib: FormLineItem): boolean {
    if (attrib.CELL_VALUE === undefined || attrib.CELL_VALUE === null) {
      return true;
    }
    const comp = attrib.LINE_COMP || attrib.UI_COMP;
    if (comp?.startsWith('NUMERIC') && attrib.CELL_VALUE === null) {
      return true;
    }
    if (['TEXT', 'DATE', 'CHK_BX', 'DROP_DOWN'].includes(comp || '') && attrib.CELL_VALUE === '') {
      return true;
    }
    return false;
  }

  private isUICompNotEmpty(attrib: FormLineItem): boolean {
    return !this.isUICompEmpty(attrib);
  }

  // P13S0: Empty function - no column totals calculation
  calcTotalsCol(columnValues: FormLineItem[]): void {
    // No calculation needed for P13S0
  }

  /**
   * Check if item is the last NUMERIC column 
   * P13S0 doesn't have auto-calculated totals, so always return false
   */
  isLastNumericColumn(items: FormLineItem[], item: FormLineItem): boolean {
    return false; // P13S0 has no auto-calculated Total column
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Allow decimal point only if not already present (matches original 'only-digits' directive)
    if (charStr === '.') {
      if (!currentValue.includes('.')) {
        return true;
      }
      return false;
    }

    // Block all other characters
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    // Allow digits, minus, and decimal point (matches original 'only-digits' directive)
    let filtered = pastedText.replace(/[^0-9.-]/g, '');
    
    // Handle minus: only one, only at start
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    
    // Handle decimal point: only one allowed
    const dotIndex = filtered.indexOf('.');
    if (dotIndex !== -1) {
      const beforeDot = filtered.substring(0, dotIndex + 1);
      const afterDot = filtered.substring(dotIndex + 1).replace(/\./g, '');
      filtered = beforeDot + afterDot;
    }
    
    item.CELL_VALUE = filtered ? parseFloat(filtered) : null;
    this.cdr.detectChanges();
  }

  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const multiRows = this.groupedFormLines.filter(
      l => l.LINE_KEY === selectedRowData.LINE_KEY && l.IS_DISPLAY === 'Y'
    );
    const sno = multiRows[multiRows.length - 1].SNO + 1;

    const tempObj: GroupedFormLine = JSON.parse(JSON.stringify(selectedRowData));
    tempObj.LINE_DESC = '';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    for (const item of tempObj.items) {
      item.OPERATION_TYPE = 'I';
      item.ADJ_KEY = null;
      this.setUICompDefaults(item);
      item.SNO = tempObj.SNO;
    }

    this.groupedFormLines.splice(index + sno + 1, 0, tempObj);
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;
    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }
    if (!isRowSaved) {
      const idx = this.groupedFormLines.indexOf(selectedRowData);
      if (idx > -1) {
        this.groupedFormLines.splice(idx, 1);
      }
    }
  }

  reset(): void {
    // Clear array before reload to prevent rendering stale data during async fetch
    this.groupedFormLines = [];
    this.showLoader = true;
    this.cdr.detectChanges();
    
    this.loadLineDetails();
  }

  saveData(): void {
    this.isSaveClicked = true;

    this.validateForm(this.groupedFormLines);

    if (this.isValidationError) {
      this.service.showError('Please fix all error(s)');
      this.isSaveClicked = false;
      return;
    }

    // Show confirmation dialog
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    this.showConfirmModal = true;
  }

  onConfirmSave(confirmed: boolean): void {
    this.showConfirmModal = false;
    if (confirmed) {
      this.performSave();
    } else {
      this.isSaveClicked = false;
    }
  }

  private performSave(): void {
    const dataToSave = this.buildDataToSave(this.groupedFormLines);

    const returnClobSettingsObj: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    // Call getLockStatus before save (matching original AngularJS behavior)
    this.service.getLockStatus(
      this.baseURLs.api,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Proceed with save after lock status check
        this.service.saveData(
          this.baseURLs.api,
          returnClobSettingsObj,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result?.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Save failed';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
          },
          error: (err) => {
            console.error('[F1065K2-P13S0] Error saving data:', err);
            this.service.showError('Error saving data');
            this.isSaveClicked = false;
          }
        });
      },
      error: (err) => {
        console.error('[F1065K2-P13S0] Lock status check error:', err);
        this.isSaveClicked = false;
      }
    });
  }

  private validateForm(groupedFormLinesObj: GroupedFormLine[]): void {
    this.isValidationError = false;
    this.isUIErrorSec = '';

    for (const line of groupedFormLinesObj) {
      line.ERR_MSG = undefined;
      let requiredMsgAlreadyAdded = false;

      // Reset errors
      for (const item of line.items) {
        item.IS_ERROR = 'N';
      }

      for (const item of line.items) {
        if (line.IS_DISPLAY === 'Y') {
          if (item.IS_ENABLED !== 'N') {
            // Mandatory validation
            if (line.IS_MANDATORY === 'Y' && this.isEmptyVal(item.CELL_VALUE)) {
              this.setErrorAttribs(line, line.items[0], 'Mandatory filed(s) are missing', requiredMsgAlreadyAdded);
              requiredMsgAlreadyAdded = true;
            }

            // Preferred mandatory columns validation
            this.validatePrefMandatoryCols(groupedFormLinesObj, line, item);

            // Special validation for LINE_NO == 'B1': value must be between 0 and 1
            if (line.LINE_NO === 'B1' && !this.isEmptyVal(item.CELL_VALUE)) {
              const val = Number(item.CELL_VALUE);
              if (val < 0 || val > 1) {
                this.setErrorAttribs(line, line.items[0], 'Value can be between 0 and 1 only', false);
              }
            }
          }
        }
      }
    }

    // Build UI error section string
    if (this.isUIErrorSec.length > 0) {
      if (this.isUIErrorSec.includes('UI_S1')) {
        this.isUIErrorSec = this.isUIErrorSec + 'UI_S1';
      }
      if (this.isUIErrorSec.includes('UI_S2')) {
        this.isUIErrorSec = this.isUIErrorSec + 'UI_S2';
      }
    }
  }

  private validatePrefMandatoryCols(lineList: GroupedFormLine[], currentLine: GroupedFormLine, currentCell: FormLineItem): void {
    if (currentLine.IS_PREF_MANDATORY === 'Y' && currentLine.PREF_MAN_COLS && currentLine.PREF_MAN_COLS.length > 0) {
      const tempArr: (any)[] = ['', '', '', ''];

      for (let i = 0; i < currentLine.PREF_MAN_COLS.length && i < 4; i++) {
        const prefCol = currentLine.PREF_MAN_COLS[i];
        const matchingLine = lineList.find(l => l.LINE_NO.startsWith(prefCol));
        if (matchingLine && matchingLine.items.length > 0) {
          tempArr[i] = matchingLine.items[0].CELL_VALUE;
        }
      }

      const isAllEmpty = tempArr.every(v => v === undefined || v === null || (typeof v === 'string' && v.length === 0));

      if (isAllEmpty) {
        this.setErrorAttribs(currentLine, null, currentLine.VALIDATION_MSG || 'Preferred mandatory field(s) are missing', false);

        for (const line of lineList) {
          if (line.IS_PREF_MANDATORY === 'Y' && currentLine.PREF_MAN_COLS &&
              currentLine.PREF_MAN_COLS.findIndex(val => val === line.LINE_NO) > -1) {
            if (line.items.length > 0) {
              line.items[0].IS_ERROR = 'Y';
            }
          }
        }
      }
    }
  }

  private setErrorAttribs(line: GroupedFormLine, col: FormLineItem | null, errMsg: string, requiredMsgAlreadyAdded: boolean): void {
    if (line.ERR_MSG === undefined) {
      line.ERR_MSG = '<ol></ol>';
      this.isUIErrorSec = this.isUIErrorSec + ',' + (line.LINE_SEC || '');
    }
    if (col !== null) {
      col.IS_ERROR = 'Y';
    }
    this.isValidationError = true;
    if (!requiredMsgAlreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', '<li>' + errMsg + '</li></ol>');
    }
    this.showErrCol = true;
  }

  private buildDataToSave(groupedFormLinesObj: GroupedFormLine[]): any[] {
    const dataToSave: any[] = [];

    for (const line of groupedFormLinesObj) {
      for (const item of line.items) {
        const returnObj: any = {};
        let cellValue = item.CELL_VALUE;

        // Match original AngularJS behavior exactly:
        // - DATE fields: format as YYYY-M-D
        // - NUMERIC/NUMERIC_INT: convert to number (original has 'only-digits' directive)
        // - Other fields: copy value "as is"
        // - If cellValue is undefined, CELL_VALUE key is not added (JS behavior)
        if (item.IS_ENABLED !== 'N') {
          const comp = item.LINE_COMP || item.UI_COMP;
          if (comp === 'DATE') {
            if (this.isUICompEmpty(item) || (this.isUICompNotEmpty(item) && cellValue instanceof Date && isNaN(cellValue.getTime()))) {
              returnObj.CELL_VALUE = '';
            } else if (cellValue instanceof Date) {
              returnObj.CELL_VALUE = `${cellValue.getFullYear()}-${cellValue.getMonth() + 1}-${cellValue.getDate()}`;
            } else {
              returnObj.CELL_VALUE = '';
            }
          } else if (comp === 'NUMERIC' || comp === 'NUMERIC_INT') {
            // NUMERIC fields: convert to number (original uses 'only-digits' directive)
            // If empty/undefined, don't add CELL_VALUE key (matches original)
            if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
              returnObj.CELL_VALUE = Number(cellValue);
            }
            // If empty, don't set CELL_VALUE - key won't be added to object
          } else {
            // All other fields: copy value as-is (matches original)
            // If cellValue is undefined, key won't be added to object
            returnObj.CELL_VALUE = cellValue;
          }
        }

        returnObj.SCHD_KEY = item.SCHD_KEY;
        returnObj.LINE_KEY = item.LINE_KEY;
        returnObj.COL_KEY = item.COL_KEY;
        returnObj.ADJ_KEY = item.ADJ_KEY;
        returnObj.SNO = item.SNO;
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE;

        if (item.OPERATION_TYPE === undefined || item.OPERATION_TYPE === null) {
          returnObj.OPERATION_TYPE = 'I';
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'I') {
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'N') {
          returnObj.OPERATION_TYPE = null;
        }

        if (returnObj.OPERATION_TYPE !== null) {
          dataToSave.push(returnObj);
        }
      }
    }

    return dataToSave;
  }

  // Helper for date change in template - uses parseAsLocalDate to avoid timezone shift
  onDateChange(item: FormLineItem, dateString: string): void {
    if (dateString) {
      item.CELL_VALUE = parseAsLocalDate(dateString);
    } else {
      item.CELL_VALUE = '';
    }
  }

  // Track by function for ngFor
  trackByLine(index: number, line: GroupedFormLine): string {
    return `${line.LINE_KEY}_${line.SNO}_${line.IS_DISPLAY}`;
  }

  trackByItem(index: number, item: FormLineItem): number {
    return item.COL_KEY;
  }
}
